"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var Components_1 = require("./Components");
/**
 * A utility function that quickly generates a custom validation function for
 * propTypes, that only allows children of a specific kind. Example usage, to
 * only allow dd and dt elements as children:
 *
 * ```js
 * const propTypes = {
 *   children: allowChildren('dd', 'dt')
 * }
 * ```
 *
 * @param types The name of all types (can be HTML or other component names) that should be allowed
 * @returns {(props: any, propName: string, componentName: string)=>Error} The generated validator to apply to propTypes
 */
function allowedChildren() {
    var types = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        types[_i] = arguments[_i];
    }
    return function (props, propName, componentName) {
        var prop = props[propName];
        var error = null;
        React.Children.forEach(prop, function (child) {
            // this will prevent null and false (empty blocks) to throw errors
            if (child) {
                // distinguish elements and pure strings to not access undefined properties of a string
                // => <A><span>test</span></A> creates a child object
                // => <A>test</A> creates a string
                var type = typeof child === 'string' ? 'string' : typeof child.type;
                var name_1 = type === 'function' ? child.type.displayName || child.type.name : child.type ? child.type : 'string';
                if ((type === 'string' || type === 'function') && types.indexOf(name_1) === -1) {
                    error = new Error(componentName + " child '" + name_1 + "' must be one of type: " + types.join(', '));
                }
            }
        });
        return error;
    };
}
exports.allowedChildren = allowedChildren;
function requireChildCount(minCount, maxCount) {
    var types = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        types[_i - 2] = arguments[_i];
    }
    var allowedChild = allowedChildren.apply(void 0, types);
    return function (props, propName, componentName) {
        var prop = props[propName];
        var count = Components_1.getChildElements(prop).length;
        // Returning the componentName (= displayName) in the errormessage
        // allows the use of dynamic names to prevent log message caching
        // see: https://github.com/facebook/react/issues/7047
        if (minCount === maxCount && count !== minCount) {
            return new Error(componentName + " expected exactly " + minCount + " children, got " + count);
        }
        else if (count < minCount) {
            return new Error(componentName + " expected more than or exactly " + minCount + " children, got " + count);
        }
        else if (count > maxCount) {
            return new Error(componentName + " expected less than or exactly " + maxCount + " children, got " + count);
        }
        return allowedChild(props, propName, componentName);
    };
}
exports.requireChildCount = requireChildCount;
/**
 * A utility function that quickly generates a custom validation function for
 * propTypes, that only requires one child of a specific kind. Example usage, to
 * only allow dd and dt elements as children:
 *
 * ```js
 * const propTypes = {
 *   children: requireSingleChild('dd', 'dt')
 * }
 * ```
 *
 * @param types The name of all types (can be HTML or other component names) that should be allowed
 * @returns {(props: any, propName: string, componentName: string)=>Error} The generated validator to apply to propTypes
 */
function requireSingleChild() {
    var types = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        types[_i] = arguments[_i];
    }
    return requireChildCount.apply(void 0, __spreadArrays([1, 1], types));
}
exports.requireSingleChild = requireSingleChild;
/**
 * A utility function that quickly generates a custom validation function for
 * propTypes, that requires a minimum amount of children of a specific kind. Example usage, to
 * require minimum one child with type of 'dt'
 *
 * ```js
 * const propTypes = {
 *   children: requireMinimumChildCount(1, 'dt')
 * }
 * ```
 * @param minCount The required minimum amount of children.
 * @param types The name of all types (can be HTML or other component names) that should be allowed
 * @returns {(props: any, propName: string, componentName: string)=>Error} The generated validator to apply to propTypes
 */
function requireMinimumChildCount(minCount) {
    var types = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        types[_i - 1] = arguments[_i];
    }
    return requireChildCount.apply(void 0, __spreadArrays([minCount, Number.MAX_VALUE], types));
}
exports.requireMinimumChildCount = requireMinimumChildCount;
//# sourceMappingURL=propTypeUtils.js.map